import React, { useEffect, useRef, useMemo } from 'react';
import throttle from 'lodash/throttle';
import { useCiTradeWebSocketStore } from '@app/hooks/ciTradeWebsocketStore.ts';
import styled from 'styled-components';
import BuyIconSrc from '../assets/BuyIcon.svg';
import SellIconSrc from '../assets/SellIcon.svg';

interface LongShortCanvasProps {
  height?: number;
}

const LongShortCanvas: React.FC<LongShortCanvasProps> = ({ height = 18 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const currentLongRef = useRef(50);
  const targetLongRef = useRef(50);
  const buyIconRef = useRef<HTMLImageElement | null>(null);
  const sellIconRef = useRef<HTMLImageElement | null>(null);

  const depthAsks = useCiTradeWebSocketStore((s) => s.depthAsks);
  const depthBids = useCiTradeWebSocketStore((s) => s.depthBids);

  // 테마 색상 (Figma 디자인 기준)
  const colors = useMemo(
    () => ({
      longBg: 'rgba(7, 217, 98, 0.08)',
      shortBg: 'rgba(244, 67, 54, 0.08)',
      longText: '#07d962',
      shortText: '#f44336',
    }),
    []
  );

  // 데이터 업데이트 (throttle)
  const throttledUpdate = useMemo(
    () =>
      throttle((bids: [string, string][], asks: [string, string][]) => {
        const longSum = bids.reduce(
          (acc, [_, amt]) => acc + parseFloat(amt),
          0
        );
        const shortSum = asks.reduce(
          (acc, [_, amt]) => acc + parseFloat(amt),
          0
        );
        const total = longSum + shortSum;
        if (total > 0) {
          targetLongRef.current = (longSum / total) * 100;
        }
      }, 500),
    []
  );

  useEffect(() => {
    if (depthBids && depthAsks) {
      throttledUpdate(depthBids, depthAsks);
    }
  }, [depthBids, depthAsks, throttledUpdate]);

  // 아이콘 이미지 로드
  useEffect(() => {
    const buyIcon = new Image();
    buyIcon.src = BuyIconSrc;
    buyIconRef.current = buyIcon;

    const sellIcon = new Image();
    sellIcon.src = SellIconSrc;
    sellIconRef.current = sellIcon;
  }, []);

  // 캔버스 애니메이션
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const h = rect.height;

      // 부드러운 이동 (easing)
      const diff = targetLongRef.current - currentLongRef.current;
      currentLongRef.current += diff * 0.08;

      const longWidth = (currentLongRef.current / 100) * width;
      const shortWidth = width - longWidth;
      const longPercent = Math.round(currentLongRef.current);
      const shortPercent = 100 - longPercent;

      // Clear
      ctx.clearRect(0, 0, width, h);

      // 롱 바 (단색 배경)
      ctx.fillStyle = colors.longBg;
      ctx.beginPath();
      ctx.roundRect(0, 0, Math.max(longWidth, 0), h, [1, 0, 0, 1]);
      ctx.fill();

      // 숏 바 (단색 배경)
      ctx.fillStyle = colors.shortBg;
      ctx.beginPath();
      ctx.roundRect(
        longWidth + 2,
        0,
        Math.max(shortWidth - 2, 0),
        h,
        [0, 1, 1, 0]
      );
      ctx.fill();

      const iconSize = 16;
      const iconY = (h - iconSize) / 2;

      // Buy 아이콘 그리기
      if (buyIconRef.current?.complete) {
        ctx.drawImage(buyIconRef.current, 1, iconY, iconSize, iconSize);
      }

      // 텍스트 스타일
      ctx.font = 'bold 10px NanumSquareRound, Pretendard, sans-serif';
      ctx.textBaseline = 'middle';

      // 롱 퍼센트 텍스트
      ctx.fillStyle = colors.longText;
      ctx.textAlign = 'left';
      ctx.fillText(`${longPercent}%`, 21, h / 2 + 1);

      // 숏 퍼센트 텍스트
      ctx.fillStyle = colors.shortText;
      ctx.textAlign = 'right';
      ctx.fillText(`${shortPercent}%`, width - 21, h / 2 + 1);

      // Sell 아이콘 그리기
      if (sellIconRef.current?.complete) {
        ctx.drawImage(
          sellIconRef.current,
          width - iconSize - 1,
          iconY,
          iconSize,
          iconSize
        );
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [colors]);

  return (
    <Container>
      <Canvas ref={canvasRef} $height={height} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 13px 10px;
  flex-shrink: 0;
`;

const Canvas = styled.canvas<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
  display: block;
`;

export default React.memo(LongShortCanvas);
